import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { API, BUDGET_RANGES, PHONE_DISPLAY, PROJECT_TYPES, START_OPTIONS, TRI_OPTIONS } from "@/lib/site";

const emptyForm = {
  name: "", phone: "", email: "", address: "", city: "",
  project_type: "", budget_range: "", start_timeframe: "",
  has_plans: "", has_engineering: "", permits_started: "", financing_interest: "",
  description: "",
};

function Segment({ label, value, onChange, testid }) {
  return (
    <div>
      <span className="form-label">{label}</span>
      <div className="grid grid-cols-3 border border-line" data-testid={testid}>
        {TRI_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            aria-pressed={value === opt}
            onClick={() => onChange(opt)}
            className={`px-2 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
              value === opt ? "bg-white text-ink" : "bg-coal text-fog hover:text-white"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LeadForm({ initialFinancing = false }) {
  const [form, setForm] = useState({ ...emptyForm, financing_interest: initialFinancing ? "Yes" : "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setSeg = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      toast.success("Your project consultation request has been received.");
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (err) {
      toast.error(`Your request could not be sent. Please call us at ${PHONE_DISPLAY}.`);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="lead-form-success-alert" className="border border-line bg-ink p-8 text-center sm:p-12">
        <CheckCircle2 className="mx-auto h-10 w-10 text-silver" />
        <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight sm:text-2xl">Request Received</h3>
        <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-mist sm:text-base">
          Thank you for contacting Black Sky Builders. A member of our team will review your project information
          and contact you to discuss the next steps.
        </p>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-steel">
          Need to reach us sooner? {PHONE_DISPLAY}
        </p>
        <button
          onClick={() => { setForm({ ...emptyForm }); setSent(false); }}
          data-testid="lead-form-submit-another-button"
          className="btn-outline mt-8"
        >
          Submit Another Project
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="lead-consultation-form" className="border border-line bg-ink p-6 sm:p-10">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="lf-name" className="form-label">Name *</label>
          <input id="lf-name" data-testid="lead-form-name-input" required value={form.name} onChange={set("name")} className="field" placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="lf-phone" className="form-label">Phone *</label>
          <input id="lf-phone" data-testid="lead-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className="field" placeholder="(801) 555-0000" />
        </div>
        <div>
          <label htmlFor="lf-email" className="form-label">Email *</label>
          <input id="lf-email" data-testid="lead-form-email-input" required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@email.com" />
        </div>
        <div>
          <label htmlFor="lf-city" className="form-label">Project City *</label>
          <input id="lf-city" data-testid="lead-form-city-input" required value={form.city} onChange={set("city")} className="field" placeholder="Salt Lake City" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="lf-address" className="form-label">Project Address</label>
          <input id="lf-address" data-testid="lead-form-address-input" value={form.address} onChange={set("address")} className="field" placeholder="Street address (optional at this stage)" />
        </div>
        <div>
          <label htmlFor="lf-type" className="form-label">Type of Project *</label>
          <select id="lf-type" data-testid="lead-form-project-type-select" required value={form.project_type} onChange={set("project_type")} className="field">
            <option value="" disabled>Select a project type</option>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="lf-budget" className="form-label">Estimated Budget</label>
          <select id="lf-budget" data-testid="lead-form-budget-select" value={form.budget_range} onChange={set("budget_range")} className="field">
            <option value="">Select a range</option>
            {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="lf-start" className="form-label">Desired Start Date</label>
          <select id="lf-start" data-testid="lead-form-start-date-select" value={form.start_timeframe} onChange={set("start_timeframe")} className="field">
            <option value="">Select a timeframe</option>
            {START_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Segment label="Architectural plans?" value={form.has_plans} onChange={setSeg("has_plans")} testid="lead-form-has-plans-toggle" />
        <Segment label="Engineering in place?" value={form.has_engineering} onChange={setSeg("has_engineering")} testid="lead-form-has-engineering-toggle" />
        <Segment label="Permits started?" value={form.permits_started} onChange={setSeg("permits_started")} testid="lead-form-permits-started-toggle" />
        <Segment label="Financing info?" value={form.financing_interest} onChange={setSeg("financing_interest")} testid="lead-form-financing-interest-toggle" />
        <div className="sm:col-span-2">
          <label htmlFor="lf-desc" className="form-label">Tell Us About the Project *</label>
          <textarea
            id="lf-desc"
            data-testid="lead-form-description-textarea"
            required
            rows={5}
            value={form.description}
            onChange={set("description")}
            className="field resize-y"
            placeholder="What do you want to build? Goals, rough size, timeline, anything you already know — and anything you're unsure about."
          />
        </div>
      </div>

      <p className="mt-5 font-mono text-[11px] leading-relaxed tracking-wide text-steel">
        Photo &amp; plan uploads are coming soon — for now, mention any existing plans, photos, or documents in your
        project description and we'll collect them when we connect.
      </p>

      <button
        type="submit"
        disabled={sending}
        data-testid="lead-form-submit-button"
        className="btn-primary mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {sending ? "Sending…" : "Request a Project Consultation"}
      </button>
    </form>
  );
}
